import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import React, { Fragment } from 'react';
import { toggleModal } from '../../app/redux/actions/globalModalAction';
import { useAppDispatch, useAppSelector } from '../../app/redux/hooks';
import { ModalContent } from './ModalContent';

type Props = {
  children: React.ReactNode;
  onClose?: () => void;
};

const GlobalModal = ({ children, onClose }: Props) => {
  const { isOpen, modalConfig, isCloseDisabled } = useAppSelector((state) => state.globalModal);
  const dispatch = useAppDispatch();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    dispatch(toggleModal(false));
  };

  const backgroundStyle = modalConfig?.fullScreen
    ? 'bg-white'
    : modalConfig?.isOverlay
      ? 'bg-gray-1100 bg-opacity-50 backdrop-blur-sm'
      : '';

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[1] w-full"
        onClose={!modalConfig?.fullScreen ? handleClose : () => null}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-50"
          leave="ease-in duration-50"
          leaveFrom="opacity-50"
          leaveTo="opacity-0"
        >
          <div className={`fixed inset-0 ${backgroundStyle}`}>
            {!isCloseDisabled && modalConfig?.fullScreen && (
              <button
                type="button"
                className="absolute right-4 top-4 md:right-6 md:top-6 opacity-20 transition-opacity hover:opacity-50 disabled:pointer-events-none"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <X className="h-5 w-5 @lg/dialog:h-6 @lg/dialog:w-6 stroke-2" aria-hidden="true" />
              </button>
            )}
          </div>
        </Transition.Child>
        <div className="flex h-full items-center justify-center text-center w-full">
          <ModalContent closeModal={handleClose}>{children}</ModalContent>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default GlobalModal;
